import React from 'react';
import { Loader, Modal, Row } from '@wunder-mobility/ui-components';
import ErrorFallback from '@/core/components/ErrorHandlers/ErrorFallback';
import { Trans } from '@lingui/react/macro';
import { FormikProvider, useFormik, Form } from 'formik';
import TripDrivingLogFormFields from '@/features/myTrips/components/TripDrivingLog/TripDrivingLogFormFields';
import { DrivingLogCreateForm } from '@/features/myTrips/types/DrivingLog';
import TripDrivingLogModalActions from '@/features/myTrips/components/TripDrivingLog/TripDrivingLogModalActions';
import { drivingLogSchema } from '@/features/myTrips/utils/validations/drivingLog.schema';
import { useNavigate, useParams, useOutletContext } from 'react-router';
import { BookingExtended } from '@/features/booking/types';
import {
  useRequestDrivingLogEntryQuery,
  useRequestUpdateDrivingLogEntryMutation,
} from '@/features/myTrips/services/DrivingLog.service';
import { parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { toDrivingLogEntryRequest } from '@/features/myTrips/utils/toDrivingLogEntryRequest';
import { useBranchTimeZoneId } from '@/features/myTrips/hooks/useBranchTimeZoneId';

const TripDrivingLogEdit = () => {
  const navigate = useNavigate();
  const { drivingLogEntryId } = useParams();
  const { rentalId, branchId } = useOutletContext<BookingExtended>();
  const { timeZoneId, isError: isTimeZoneError } = useBranchTimeZoneId(branchId);
  const { data: drivingLogEntry } = useRequestDrivingLogEntryQuery(
    { rentalId: rentalId!, drivingLogEntryId: +drivingLogEntryId! },
    { skip: !rentalId || !drivingLogEntryId },
  );

  const [updateDrivingLogEntry] = useRequestUpdateDrivingLogEntryMutation();

  const onSubmit = (values: DrivingLogCreateForm) => {
    // The form is only rendered once the zone is known; this guards the submit itself.
    if (!timeZoneId) {
      return undefined;
    }

    return updateDrivingLogEntry({
      ...toDrivingLogEntryRequest({ ...values, startDate: values.startDate! }, timeZoneId),
      rentalId: rentalId!,
      drivingLogEntryId: +drivingLogEntryId!,
    })
      .unwrap()
      .then(() => {
        navigate(-1);
      });
  };

  const form = useFormik<DrivingLogCreateForm>({
    initialValues: {
      // Read the branch's calendar day and time as text, so the browser's zone (and its DST gaps)
      // never shifts them. The date picker only needs the day.
      startDate:
        drivingLogEntry?.startedAt && timeZoneId
          ? parse(
              formatInTimeZone(drivingLogEntry.startedAt, timeZoneId, 'yyyy-MM-dd'),
              'yyyy-MM-dd',
              new Date(),
            )
          : undefined,
      startTime:
        drivingLogEntry?.startedAt && timeZoneId
          ? formatInTimeZone(drivingLogEntry.startedAt, timeZoneId, 'HH:mm')
          : undefined,
      endLocation: drivingLogEntry?.endLocation,
      startLocation: drivingLogEntry?.startLocation,
      reason: drivingLogEntry?.reason,
      startKilometers: drivingLogEntry?.startKilometers
        ? parseFloat(drivingLogEntry.startKilometers.toFixed(2))
        : undefined,
      endKilometers: drivingLogEntry?.endKilometers
        ? parseFloat(drivingLogEntry.endKilometers.toFixed(2))
        : undefined,
      note: drivingLogEntry?.note,
      tripType: drivingLogEntry?.tripType,
    },
    enableReinitialize: true,
    validationSchema: drivingLogSchema,
    onSubmit,
  });

  return (
    <FormikProvider value={form}>
      <Modal
        modalHeader={<Trans id="myTrips.tripDrivingLogEdit.title">Edit driving log</Trans>}
        modalFooter={<TripDrivingLogModalActions mode="edit" />}
        variation="small"
        onClose={() => navigate(-1)}
        opened
      >
        {isTimeZoneError && <ErrorFallback />}
        {!isTimeZoneError && !timeZoneId && (
          <Row justify="center">
            <Loader />
          </Row>
        )}
        {timeZoneId && (
          <Form>
            {form.isSubmitting && <Loader cover />}
            <TripDrivingLogFormFields />
          </Form>
        )}
      </Modal>
    </FormikProvider>
  );
};

export default TripDrivingLogEdit;
